<template>
  <div>
    <Board class="mb-6">
      <template #header class="mb-6">
        <div class="w_header">
          <BaseTitle v-text="$t('automation.title')" />
        </div>
      </template>
      <template #main>
        <BaseDivider class="my-5" :grey="true" />
        <div class="automation_section">
          <div class="action">
            <BaseButton
              class="menu-action"
              icon="$mdiPlus"
              @click="openMenu = !openMenu"
            >
              {{ $t("automation.create_btn_label") }}
              <div v-show="openMenu" class="item_contain">
                <div
                  v-for="(effect, index) in effectItems"
                  :key="index"
                  class="item-list"
                  @click.stop="openEffectModal(effect.value)"
                >
                  <BaseParagraph type="primary" v-text="effect.label" />
                </div>
              </div>
            </BaseButton>
          </div>
        </div>
        <div class="effect_list">
          <BaseDataTable
            :items="effectDetails.items"
            :loading="effectFilter.loading"
            :loading-text="$t('utils.loadingData')"
            hide-default-footer
            :items-per-page="-1"
          >
            <template #header>
              <thead>
                <tr>
                  <th>
                    {{ $tc("utils.type", 1) }}
                  </th>
                  <th>
                    {{ $tc("utils.label") }}
                  </th>
                  <th>
                    {{ $t("automation.trigger_once") }}
                  </th>
                  <th>
                    {{ $tc("utils.status", 2) }}
                  </th>
                  <th>{{ $tc("utils.trigger") }}</th>
                  <th class="text-end">
                    {{ $t("utils.actions") }}
                  </th>
                </tr>
              </thead>
            </template>
            <template #item="{ item }">
              <tr class="tab_row">
                <td>
                  <div class="row_item">
                    <div class="item_label" v-text="$t(item.effectLabel)" />
                    <div
                      v-if="item.effectExtraLabel"
                      class="item_extra"
                      v-text="item.effectExtraLabel"
                    />
                    <div
                      v-if="item.xmlPattern"
                      class="item_link"
                      @click="displayXmlPattern(item.xmlPattern)"
                      v-text="$t('utils.display_xml_template')"
                    />
                  </div>
                </td>

                <td>
                  <div class="row_item">
                    <div
                      class="item_label"
                      v-text="
                        item.label ? $t(item.label) : $t('utils.noContent')
                      "
                    />
                  </div>
                </td>

                <td>
                  <div class="row_item">
                    <input
                      v-if="item.unique"
                      v-model="item.unique"
                      disabled
                      class="unique_input"
                      type="checkbox"
                    />
                    <div v-else>-</div>
                  </div>
                </td>
                <td>
                  <div class="row_item">
                    <div class="item_label" v-text="item.statusesLabel" />
                  </div>
                </td>
                <td>
                  <div class="row_item">
                    <div class="item_label" v-text="$t(item.eventLabel)" />
                    <div class="item_extra" v-text="item.eventExtratLabel" />
                  </div>
                </td>
                <td>
                  <div class="action_section">
                    <BaseButton
                      small
                      color="primary"
                      @click="openAutomation(item)"
                    >
                      {{ $tc("utils.automate", { action_name: "" }) }}
                    </BaseButton>
                    <BaseButton
                      small
                      color="primary"
                      @click="openEditModal(item)"
                    >
                      {{ $tc("utils.edit") }}
                    </BaseButton>
                    <BaseButton
                      color="secondary"
                      class="ml-2"
                      @click="openRemoveEffectDialog(item)"
                    >
                      {{ $tc("utils.delete") }}
                    </BaseButton>
                  </div>
                </td>
              </tr>
            </template>
          </BaseDataTable>
        </div>
      </template>
    </Board>
    <AutomationRemoveDialog
      v-if="workflow && removeEffectDialogState"
      :automation-effect="selectedEffect"
      :organization-id="organizationId"
      :workflow-id="workflow.id"
      @close="removeEffectDialogState = false"
      @effectRemoved="onEffectRemoved"
    />

    <XMLPatternPreviewer
      v-if="openXmlPatternDialog"
      :xml-pattern="xmlPattern"
      @close="openXmlPatternDialog = false"
    />
    <EffectCreateAndEditDialog
      v-if="effectModalOpen"
      :organization-id="organizationId"
      :workflow="workflow"
      :selected-effect="effectSelected"
      :automation-action-to-edit="automationActionToEdit"
      @effect-edited="editEffect"
      @close="closeEffectModal"
      @effect-created="onEffectCreated"
    />
    <EffectAutomationDialog
      v-if="openEffectAutomationModal"
      :organization-id="organizationId"
      :workflow="workflow"
      :automation-action-to-edit="automationActionToEdit"
      @close="closeAutomateModal"
      @effect-edited="editEffect"
    />
    <EffectSignatureCreateAndEditDialog
      v-if="effectSignatureOpen"
      :organization-id="organizationId"
      :workflow="workflow"
      :automation-action-to-edit="automationActionToEdit"
      @effect-edited="editEffect"
      @close="closeEffectModal"
      @effect-created="onEffectCreated"
    />
  </div>
</template>

<script>
import { AutomationEffect } from "@/core/Models/AutomationEffect";
import workflowApi from "../Services/workflow.api";
import XMLPatternPreviewer from "@/modules/Admin/Components/XMLPatternPreviewer.vue";
import AutomationRemoveDialog from "@/modules/Admin/Components/AutomationRemoveDialog.vue";
import Board from "@/core/Components/Board.vue";
import { AUTOMATION_EFFECT } from "@/core/Utils/types.utils";
import EffectCreateAndEditDialog from "./EffectCreateAndEditDialog.vue";
import EffectAutomationDialog from "./EffectAutomationDialog.vue";
import EffectSignatureCreateAndEditDialog from "./EffectSignatureCreateAndEditDialog.vue";

export default {
  name: "EffectManagement",
  components: {
    Board,
    AutomationRemoveDialog,
    XMLPatternPreviewer,
    EffectCreateAndEditDialog,
    EffectAutomationDialog,
    EffectSignatureCreateAndEditDialog,
  },
  props: {
    organizationId: {
      type: [Number, String],
      required: true,
    },
    workflow: {
      type: Object,
      required: true,
    },
    automationEffects: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      removeEffectDialogState: false,
      selectedEffect: null,
      automationActionToEdit: null,
      openXmlPatternDialog: false,
      openEffectAutomationModal: false,
      openMenu: false,
      effectDetails: {
        items: [],
      },
      effectFilter: {
        page: 1,
        loading: false,
      },
      effectItems: [
        {
          label: this.$t("automation.effect.set_status"),
          value: AUTOMATION_EFFECT.SET_STATUS,
        },
        {
          label: this.$t("automation.effect.send_offer"),
          value: AUTOMATION_EFFECT.SEND_OFFER,
        },
        {
          label: this.$t("automation.effect.send_signature"),
          value: AUTOMATION_EFFECT.SEND_SIGNATURE,
        },
        {
          label: this.$t("automation.effect.send_form"),
          value: AUTOMATION_EFFECT.SEND_FORM,
        },
        {
          label: this.$t("automation.effect.send_to_ged"),
          value: AUTOMATION_EFFECT.SEND_TO_GED,
          // disabled: !this.hasEnoxa,
        },
        {
          label: this.$t("automation.effect.archive_folder"),
          value: AUTOMATION_EFFECT.ARCHIVE_FOLDER,
        },
        {
          label: this.$t("automation.effect.send_campaign"),
          value: AUTOMATION_EFFECT.SEND_CAMPAIGN,
        },
        {
          label: this.$t("automation.effect.generate_doc"),
          value: AUTOMATION_EFFECT.GENERATE_DOC,
        },
        {
          label: this.$t("automation.effect.assign_collaborator"),
          value: AUTOMATION_EFFECT.ASSIGN_COLLABORATOR,
        },
      ],
      effectSelected: null,
      effectModalOpen: false,
      effectSignatureOpen: false,
    };
  },
  created() {
    this.effectDetails.items = this.automationEffects.map(
      (ae) => new AutomationEffect({ ...ae })
    );
  },
  methods: {
    openEditModal(item) {
      if (item.action === "SEND_OFFER" && item.actionConfig)
        this.effectSignatureOpen = true;
      else this.effectModalOpen = true;
      this.automationActionToEdit = { ...item };
      this.effectSelected = item.action;
    },
    editEffect({ item, value }) {
      const indexToEdit = this.effectDetails.items.findIndex(
        (effect) => effect.id === item
      );
      this.effectDetails.items.splice(
        indexToEdit,
        1,
        new AutomationEffect(value)
      );
    },
    closeEffectModal() {
      if (
        this.effectSelected == AUTOMATION_EFFECT.SEND_SIGNATURE ||
        (this.automationActionToEdit?.action &&
          this.automationActionToEdit?.actionConfig)
      )
        this.effectSignatureOpen = false;
      else this.effectModalOpen = false;
      this.effectModalOpen = false;
      this.automationActionToEdit = null;
    },
    closeAutomateModal() {
      this.openEffectAutomationModal = false;
      this.automationActionToEdit = null;
    },

    async fetchWorkflowEffect() {
      this.effectFilter.loading = true;
      try {
        const res = await workflowApi.getOrganizationWorkflowEffect({
          organizationId: this.organizationId,
          workflowId: this.workflow.id,
          params: {
            page: this.effectFilter.page,
          },
        });
        this.effectDetails = {
          ...res.data,
          items: res.data.results.map((d) => new AutomationEffect(d)),
        };
        this.$emit("updatedAutomationEffects", this.effectDetails.items);
      } catch (error) {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.effectFilter.loading = false;
    },
    async onEffectCreated() {
      this.effectFilter.page = 1;
      await this.fetchWorkflowEffect();
    },
    openRemoveEffectDialog(effect) {
      this.selectedEffect = effect;
      this.removeEffectDialogState = true;
    },
    onEffectRemoved(effect) {
      this.removeEffectDialogState = false;
      this.effectDetails.items = this.effectDetails.items.filter(
        (e) => e.id != effect.id
      );
      this.$emit("updatedAutomationEffects", this.effectDetails.items);
    },
    displayXmlPattern(xmlPattern) {
      this.xmlPattern = xmlPattern;
      this.openXmlPatternDialog = true;
    },
    openEffectModal(item) {
      if (item == AUTOMATION_EFFECT.SEND_SIGNATURE)
        this.effectSignatureOpen = true;
      else this.effectModalOpen = true;
      this.effectSelected = item;
      this.openMenu = !this.openMenu;
    },
    openAutomation(item) {
      this.automationActionToEdit = { ...item };
      this.openEffectAutomationModal = true;
    },
  },
};
</script>

<style lang="scss" scoped>
.w_header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
}
.inactivity_section {
  padding: 24px;
  background: #fff;
  margin-bottom: 24px;
  .header_section {
    display: flex;
    justify-content: space-between;
  }
  .alert_label {
    color: #353542;
    font-family: Inter;
    font-size: 14px;
    font-weight: 400;
    line-height: 17px;
    display: flex;
    gap: 4px;
    align-items: center;

    .days {
      font-weight: bold;
      color: #ff5267;
      line-height: 14px;
      padding: 4px 6px;
      border-radius: 4px;
      background: rgba(#ff5267, 0.2);
    }
  }
}
.automation_section {
  .action {
    display: flex;
    justify-content: flex-end;
    .menu-action {
      position: relative;
      .item_contain {
        position: absolute;
        z-index: 100;
        right: -14px;
        top: 30px;
        border-radius: 5px;
        width: 200px;
        background: #fff;
        box-shadow: 0px 6px 10px 0px rgba(46, 45, 193, 0.06),
          0px 2px 4px 0px rgba(46, 45, 193, 0.1);
        .item-list {
          padding: 12px;
          height: 45px;
        }
      }
    }
  }
}
.effect_list {
  padding-top: 16px;
  .tab_row {
    .row_item {
      padding: 16px 0;
      font-family: "Inter";
      font-style: normal;
      .item_label {
        color: #353542;
        font-size: 12px;
        font-weight: 600;
        line-height: 20px;
      }
      .item_extra {
        color: #707080;
        font-size: 12px;
        font-weight: 400;
        line-height: 16px;
        text-transform: uppercase;
      }
      .item_link {
        color: #2536cc;
        font-size: 12px;
        font-weight: 400;
        line-height: 16px;
        text-decoration: underline;
        cursor: pointer;
      }
      .unique_input {
        width: 18px;
        height: 18px;
      }
    }
    .action_section {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 4px;
    }
  }
}

// new
.item-list {
  display: flex;
  align-items: center;
  height: 33px;
  cursor: pointer;
  &:hover {
    background: #f4f7ff;
  }
}
</style>
